// src/components/common/LeavePolicyModal.js
import React, { useState, useEffect } from 'react';
import { X, FileText, Edit2, Save, Loader, Trash2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import leavePolicyService from '../../services/leavePolicy.service';

const LeavePolicyModal = ({
    isOpen,
    onClose,
    isAdmin = false,
    leavePlanId = null, // For admin view - specific leave plan
    leaveTypes = [] // For admin view - leave types to auto-fill
}) => {
    const { theme } = useTheme();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [policyData, setPolicyData] = useState(null);
    const [planName, setPlanName] = useState('');

    // Form state for editing
    const [formData, setFormData] = useState({
        policy_name: '',
        description: '',
        rules: []
    });

    useEffect(() => {
        if (isOpen) {
            loadPolicyData();
        }
    }, [isOpen, leavePlanId]);

    const loadPolicyData = async () => {
        setLoading(true);
        try {
            if (isAdmin && leavePlanId) {
                // Admin view - fetch policy for specific leave plan
                const response = await leavePolicyService.getPolicyByLeavePlan(leavePlanId);
                setPolicyData(response.policy);
                setPlanName(response.plan?.plan_name || '');

                if (response.policy) {
                    // Existing policy - load it
                    setFormData({
                        policy_name: response.policy.policy_name || '',
                        description: response.policy.description || '',
                        rules: response.policy.rules || []
                    });
                    setIsEditMode(false);
                } else {
                    // No policy exists - auto-fill from leave types and enter edit mode
                    autoFillFromLeaveTypes();
                    setIsEditMode(true);
                }
            } else {
                // Employee view - fetch their policy
                const response = await leavePolicyService.getMyPolicy();
                setPolicyData(response.policy);
                setPlanName(response.policy?.plan_name || response.plan_name || '');

                if (response.policy) {
                    // Existing policy - load it
                    setFormData({
                        policy_name: response.policy.policy_name || '',
                        description: response.policy.description || '',
                        rules: response.policy.rules || []
                    });
                    setIsEditMode(false);
                } else if (response.leave_types && response.leave_types.length > 0) {
                    // No policy but have leave types - auto-fill with detailed notes
                    const rules = response.leave_types.map(type => ({
                        leave_plan_type_id: type.id,
                        leave_type_name: type.leave_type_name,
                        leave_code: type.leave_code,
                        total_days: type.total_days || 0,
                        carry_forward: type.carry_forward || false,
                        max_carry_forward_days: type.max_carry_forward_days || 0,
                        encashable: type.encashable || false,
                        requires_approval: type.requires_approval !== false,
                        min_days_notice: type.min_days_notice || 0,
                        max_consecutive_days: type.max_consecutive_days || null,
                        prorate_on_joining: type.prorate_on_joining !== false,
                        policy_notes: getDefaultPolicyNotes(type.leave_type_name, type.leave_code)
                    }));

                    setFormData({
                        policy_name: `${response.plan_name} Leave Policy`,
                        description: 'This comprehensive leave policy outlines the types of leaves available, eligibility criteria, entitlements, accrual methods, and procedures for requesting and availing leave. All employees are expected to familiarize themselves with this policy and follow the prescribed procedures.',
                        rules
                    });
                    setIsEditMode(false); // Show in read-only mode with auto-filled data
                } else {
                    setIsEditMode(false);
                }
            }
        } catch (error) {
            console.error('Error loading policy:', error);
        } finally {
            setLoading(false);
        }
    };

    const getDefaultPolicyNotes = (leaveTypeName, leaveCode) => {
        const normalizedType = leaveTypeName.toLowerCase();

        if (normalizedType.includes('casual') || leaveCode === 'CL') {
            return 'Accrual: 1 day per month. Purpose: Short-term personal matters, unforeseen circumstances. Can be availed in half-day increments. Medical certificate required for more than 2 consecutive days.';
        } else if (normalizedType.includes('paid') || normalizedType.includes('privilege') || leaveCode === 'PL') {
            return 'Accrual: Pro-rated monthly. Purpose: Planned vacations, personal time. Must be applied in advance. Can be combined with other leaves. Club with weekends and holidays for extended vacation.';
        } else if (normalizedType.includes('sick') || leaveCode === 'SL') {
            return 'No accrual - available from day one. Purpose: Illness, medical appointments, recovery. Medical certificate required for more than 2 consecutive days. Cannot be carried forward to next year.';
        } else if (normalizedType.includes('comp') || normalizedType.includes('compensatory') || leaveCode === 'CO') {
            return 'Earned by working on holidays or weekends with prior approval. Must be availed within 90 days of earning. Requires manager approval for crediting. Cannot be carried forward. Must be pre-approved before working on off-days.';
        } else if (normalizedType.includes('floater') || leaveCode === 'FL') {
            return 'Purpose: Personal/religious occasions, festivals. Can be availed throughout the year. Must be applied at least 1 day in advance. Cannot be carried forward. Subject to operational requirements and manager approval.';
        } else if (normalizedType.includes('marriage') || normalizedType.includes('wedding')) {
            return 'For own marriage only. Must be availed within 1 year of marriage date. Proof of marriage required. Can be split but must be availed in minimum 2-day blocks. Non-transferable.';
        } else if (normalizedType.includes('maternity')) {
            return '26 weeks as per Maternity Benefit Act. Must be applied at least 6 weeks before expected delivery date. Medical certificate required. Can be extended with medical recommendation. Includes pre and post-natal leave.';
        } else if (normalizedType.includes('paternity')) {
            return '15 days within 6 months of childbirth. Must be availed in one continuous block or two separate blocks. Birth certificate/adoption papers required. Can be split with minimum 3-day blocks.';
        } else if (normalizedType.includes('bereavement') || normalizedType.includes('condolence')) {
            return 'For immediate family members (parents, spouse, children, siblings). Death certificate required. Additional days may be granted for distant locations. Can be extended in exceptional circumstances.';
        } else {
            return 'Please refer to company leave policy for detailed guidelines and eligibility criteria. Contact HR for any clarifications.';
        }
    };

    const autoFillFromLeaveTypes = () => {
        if (leaveTypes && leaveTypes.length > 0) {
            const rules = leaveTypes.map(type => ({
                leave_plan_type_id: type.id,
                leave_type_name: type.leave_type_name,
                leave_code: type.leave_code,
                total_days: type.total_days || 0,
                carry_forward: type.carry_forward || false,
                max_carry_forward_days: type.max_carry_forward_days || 0,
                encashable: type.encashable || false,
                requires_approval: type.requires_approval !== false,
                min_days_notice: type.min_days_notice || 0,
                max_consecutive_days: type.max_consecutive_days || null,
                prorate_on_joining: type.prorate_on_joining !== false,
                policy_notes: getDefaultPolicyNotes(type.leave_type_name, type.leave_code)
            }));

            setFormData({
                policy_name: `${planName} Leave Policy`,
                description: 'This comprehensive leave policy outlines the types of leaves available, eligibility criteria, entitlements, accrual methods, and procedures for requesting and availing leave. All employees are expected to familiarize themselves with this policy and follow the prescribed procedures.',
                rules
            });
        }
    };

    const handleSave = async () => {
        if (!formData.policy_name || formData.rules.length === 0) {
            alert('Policy name and at least one leave type are required');
            return;
        }

        setSaving(true);
        try {
            await leavePolicyService.createOrUpdatePolicy(leavePlanId, formData);
            alert('Leave policy saved successfully');
            await loadPolicyData();
            setIsEditMode(false);
        } catch (error) {
            console.error('Error saving policy:', error);
            alert(error.response?.data?.message || 'Failed to save leave policy');
        } finally {
            setSaving(false);
        }
    };

    const handleRuleChange = (index, field, value) => {
        const updatedRules = [...formData.rules];
        updatedRules[index] = { ...updatedRules[index], [field]: value };
        setFormData({ ...formData, rules: updatedRules });
    };

    const handleDeleteRule = (index) => {
        const updatedRules = formData.rules.filter((_, i) => i !== index);
        setFormData({ ...formData, rules: updatedRules });
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={onClose} />
            <div className={`fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-xl shadow-2xl z-50 p-6 max-w-5xl w-full max-h-[90vh] overflow-y-auto ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-white'
            }`}>
                <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center gap-3">
                        <FileText className={`w-6 h-6 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
                        <div>
                            <h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                Leave Policy
                            </h2>
                            {planName && (
                                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {planName}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {isAdmin && policyData && !isEditMode && (
                            <button
                                onClick={() => setIsEditMode(true)}
                                className={`p-2 rounded-lg ${
                                    theme === 'dark'
                                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                                }`}
                            >
                                <Edit2 className="w-5 h-5" />
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            className={`${theme === 'dark' ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader className={`w-8 h-8 animate-spin ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
                    </div>
                ) : !policyData && !isEditMode && formData.rules.length === 0 ? (
                    <div className="text-center py-12">
                        <FileText className={`w-16 h-16 mx-auto mb-4 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
                        <p className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            No leave policy available
                        </p>
                        <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            {isAdmin ? 'Click "Edit" to create a policy for this leave plan' : 'Please contact HR for more information'}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Policy Name and Description */}
                        {isEditMode ? (
                            <>
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                        Policy Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.policy_name}
                                        onChange={(e) => setFormData({ ...formData, policy_name: e.target.value })}
                                        className={`w-full px-3 py-2 rounded-lg border ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                        placeholder="e.g., Standard Leave Policy 2025"
                                    />
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                        Description
                                    </label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        rows="2"
                                        className={`w-full px-3 py-2 rounded-lg border ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                        placeholder="Brief description of the leave policy"
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <div>
                                    <h3 className={`text-lg font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                        {formData.policy_name}
                                    </h3>
                                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                        {formData.description}
                                    </p>
                                </div>
                            </>
                        )}

                        {/* Leave Types */}
                        <div>
                            <h3 className={`text-lg font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                Leave Types & Entitlements
                            </h3>
                            <div className="space-y-4">
                                {formData.rules.map((rule, index) => (
                                    <div
                                        key={index}
                                        className={`border rounded-lg p-4 ${
                                            theme === 'dark' ? 'border-gray-700 bg-gray-750' : 'border-gray-200'
                                        }`}
                                    >
                                        <div className="flex justify-between items-start mb-3">
                                            <h4 className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                {index + 1}. {rule.leave_type_name} ({rule.leave_code})
                                            </h4>
                                            {isEditMode && (
                                                <button
                                                    onClick={() => handleDeleteRule(index)}
                                                    className="text-red-500 hover:text-red-700"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>

                                        {isEditMode ? (
                                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                                <div>
                                                    <label className={`block text-xs mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                                        Total Days *
                                                    </label>
                                                    <input
                                                        type="number"
                                                        value={rule.total_days}
                                                        onChange={(e) => handleRuleChange(index, 'total_days', parseInt(e.target.value) || 0)}
                                                        className={`w-full px-2 py-1 text-sm rounded border ${
                                                            theme === 'dark'
                                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                                : 'bg-white border-gray-300 text-gray-900'
                                                        }`}
                                                    />
                                                </div>
                                                <div>
                                                    <label className={`block text-xs mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                                        Min Notice (days)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        value={rule.min_days_notice}
                                                        onChange={(e) => handleRuleChange(index, 'min_days_notice', parseInt(e.target.value) || 0)}
                                                        className={`w-full px-2 py-1 text-sm rounded border ${
                                                            theme === 'dark'
                                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                                : 'bg-white border-gray-300 text-gray-900'
                                                        }`}
                                                    />
                                                </div>
                                                <div>
                                                    <label className={`block text-xs mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                                        Max Carry Forward
                                                    </label>
                                                    <input
                                                        type="number"
                                                        value={rule.max_carry_forward_days}
                                                        onChange={(e) => handleRuleChange(index, 'max_carry_forward_days', parseInt(e.target.value) || 0)}
                                                        className={`w-full px-2 py-1 text-sm rounded border ${
                                                            theme === 'dark'
                                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                                : 'bg-white border-gray-300 text-gray-900'
                                                        }`}
                                                    />
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={rule.carry_forward}
                                                        onChange={(e) => handleRuleChange(index, 'carry_forward', e.target.checked)}
                                                        className="w-4 h-4"
                                                    />
                                                    <label className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                                        Carry Forward
                                                    </label>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={rule.encashable}
                                                        onChange={(e) => handleRuleChange(index, 'encashable', e.target.checked)}
                                                        className="w-4 h-4"
                                                    />
                                                    <label className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                                        Encashable
                                                    </label>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={rule.prorate_on_joining}
                                                        onChange={(e) => handleRuleChange(index, 'prorate_on_joining', e.target.checked)}
                                                        className="w-4 h-4"
                                                    />
                                                    <label className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                                        Prorate on Joining
                                                    </label>
                                                </div>
                                                <div className="col-span-2 md:col-span-3">
                                                    <label className={`block text-xs mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                                        Policy Notes
                                                    </label>
                                                    <textarea
                                                        value={rule.policy_notes || ''}
                                                        onChange={(e) => handleRuleChange(index, 'policy_notes', e.target.value)}
                                                        rows="2"
                                                        className={`w-full px-2 py-1 text-sm rounded border ${
                                                            theme === 'dark'
                                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                                : 'bg-white border-gray-300 text-gray-900'
                                                        }`}
                                                        placeholder="Additional notes or requirements..."
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <ul className={`list-disc list-inside text-sm space-y-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                                <li>Annual Quota: {rule.total_days} days per year</li>
                                                {rule.carry_forward && (
                                                    <li>Carry Forward: Up to {rule.max_carry_forward_days} days</li>
                                                )}
                                                {rule.min_days_notice > 0 && (
                                                    <li>Minimum Notice: {rule.min_days_notice} day{rule.min_days_notice > 1 ? 's' : ''} in advance</li>
                                                )}
                                                {rule.encashable && <li>Encashable: Yes</li>}
                                                {rule.prorate_on_joining && <li>Pro-rated for new joiners</li>}
                                                {rule.policy_notes && <li>{rule.policy_notes}</li>}
                                            </ul>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Important Notes */}
                        {!isEditMode && (
                            <div>
                                <h3 className={`text-lg font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                    Important Notes
                                </h3>
                                <div className={`border rounded-lg p-4 ${
                                    theme === 'dark'
                                        ? 'bg-yellow-900/20 border-yellow-800'
                                        : 'bg-yellow-50 border-yellow-200'
                                }`}>
                                    <ul className={`list-disc list-inside text-sm space-y-1 ${
                                        theme === 'dark' ? 'text-yellow-300' : 'text-yellow-800'
                                    }`}>
                                        <li>Leaves are subject to manager approval</li>
                                        <li>Leave balance is calculated on pro-rata basis for new joiners</li>
                                        <li>Unauthorized absence will be marked as Leave Without Pay (LWP)</li>
                                        <li>Company reserves the right to modify the policy with notice</li>
                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end gap-2 pt-6">
                    {isEditMode ? (
                        <>
                            <button
                                onClick={() => {
                                    setIsEditMode(false);
                                    if (policyData) {
                                        // Restore original data
                                        setFormData({
                                            policy_name: policyData.policy_name || '',
                                            description: policyData.description || '',
                                            rules: policyData.rules || []
                                        });
                                    } else {
                                        onClose();
                                    }
                                }}
                                disabled={saving}
                                className={`px-4 py-2 rounded-lg ${
                                    theme === 'dark'
                                        ? 'bg-gray-600 hover:bg-gray-700 text-white'
                                        : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                                }`}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                            >
                                {saving ? (
                                    <>
                                        <Loader className="w-4 h-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        Save Policy
                                    </>
                                )}
                            </button>
                        </>
                    ) : (
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            Close
                        </button>
                    )}
                </div>
            </div>
        </>
    );
};

export default LeavePolicyModal;
